import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-medium ring-offset-background transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_4px_20px_-4px_hsla(210,29%,24%,0.1)] hover:shadow-[0_8px_30px_-4px_hsla(184,77%,22%,0.3)]",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border-2 border-primary bg-transparent text-primary hover:bg-primary hover:text-primary-foreground",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent/10 hover:text-accent",
        link: "text-primary underline-offset-4 hover:underline",
        hero: "bg-accent text-accent-foreground hover:bg-accent/90 shadow-[0_8px_30px_-4px_hsla(14,69%,63%,0.4)] hover:shadow-[0_20px_50px_-12px_hsla(210,29%,24%,0.2)] font-semibold text-base",
        heroOutline: "border-2 border-accent-foreground/80 bg-transparent text-accent-foreground hover:bg-accent-foreground/10 backdrop-blur-sm",
        tropical: "bg-gradient-to-br from-[hsl(184,77%,22%)] to-[hsl(184,77%,32%)] text-primary-foreground hover:opacity-90 shadow-[0_8px_30px_-4px_hsla(184,77%,22%,0.3)]",
        sunset: "bg-gradient-to-br from-[hsl(14,69%,63%)] to-[hsl(25,80%,55%)] text-accent-foreground hover:opacity-90 shadow-[0_8px_30px_-4px_hsla(14,69%,63%,0.4)]",
        palm: "bg-[hsl(134,24%,29%)] text-[hsl(40,33%,97%)] hover:bg-[hsl(134,24%,29%)]/90",
        sand: "bg-secondary text-foreground hover:bg-secondary/70 border border-border",
      },
      size: {
        default: "h-11 px-6 py-2",
        sm: "h-9 rounded-lg px-4",
        lg: "h-12 rounded-xl px-8 text-base",
        xl: "h-14 rounded-2xl px-10 text-lg",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
